import { Stack } from '@mui/material'
import { useState } from 'react'
import { IconButton } from 'utoniq-ui'

import { Maybe, UserCollectionBoxFragment } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenu, SideMenuProps } from '@/components/layouts'
import { CollectionBox, CollectionBoxSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  userCollections: Maybe<{ node: UserCollectionBoxFragment }[]>
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  user: Maybe<SideMenuProps['user']>
  isArtist: Maybe<SideMenuProps['isArtist']>
}

type CollectionBannerCellProps = Pick<
  State,
  'loading' | 'hasNextPage' | 'userCollections'
> &
  Action

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
}

export const GlobalCollectionsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
}) => {
  const { t } = useTranslation('globalCollections')
  const { routerNames } = useRouter()
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)

  return (
    <>
      {/**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-4516
       */}
      <BaseLayout
        navigation={{
          type: 'global',
          props: {
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
            hasSettingTask:
              !!state.user?.isMembershipPersonalInformationRequired,
          },
        }}
        margin
        header={{
          title: t('title'),
          left: config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!openSideMenu)}
              color="icon.line"
              bgcolor="icon.background"
              badge={
                state.user?.hasManagementTask ||
                !!state.user?.unreadNotificationCount ||
                !!state.user?.isMembershipPersonalInformationRequired
              }
            />
          ),
          // right: <IconButton.Search edge="end" />,
        }}
      >
        <Head title={routerNames.GlobalCollections} />
        <SideMenu
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          isArtist={state.isArtist}
        />
        <CollectionBannerCell
          userCollections={state.userCollections}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
        />
      </BaseLayout>
    </>
  )
}

const CollectionBannerCell: Cell<CollectionBannerCellProps> = ({
  loading,
  hasNextPage,
  userCollections,
  onFetchMore,
}) => {
  const { t } = useTranslation('globalCollections')
  const { analytics } = useAnalytics()
  return (
    <CellFrame sx={{ pt: 2, pb: '60px' }}>
      <ScrollFetcher
        nodes={userCollections?.map(({ node: collection }, index) => (
          <CollectionBox
            key={index}
            collection={collection}
            fullWidth
            sx={{
              height: 'calc((100vw - 40px) * 190 / 335)',
              maxHeight: 'calc(560px * 190 / 335)',
            }}
            actionAreaProps={{
              onClick() {
                analytics?.track('click_select', {
                  applicationKey: config.applicationKey,
                  artistId: collection.collection?.artistId,
                  collectionId: collection.id,
                  name: 'globalCollections',
                })
              },
            }}
          />
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        componentProps={{ spacing: 5 }}
        emptyMessage={t('collectionEmptyMessage')}
        loadingComponent={
          <Stack spacing={6} flexDirection="column" sx={{ py: 2 }}>
            {Array.from(Array(3).keys()).map(key => (
              <CollectionBoxSkeleton fullWidth key={key} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}
